import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, PLATFORM_ID, inject } from '@angular/core';
import type { SiteWindow } from '@core-mkt/interfaces/site-window';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EnvService } from '@core-mkt/services/env/env.service';
import { StorageService } from '@core-mkt/services/storage/storage.service';
import { MarketingSyncUserService } from '@core-mkt/services/sync-user/marketing-sync-user.service';
import { AmbassadorGetService } from '@core-mkt/services/ambassador-get/ambassador-get.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCopy as faCopyIcon, faX } from '@fortawesome/free-solid-svg-icons';

const DISMISSED_KEY = 'dismissed-referral-popup';
const UTM_SUFFIX = '&utm_medium=referral&utm_campaign=A_D_S_Help_B';
const ACE_DEV_AMBASSADOR_INTEGRATION_ID = 48935;

@Component({
  selector: 'referral-popup',
  templateUrl: './referral-popup.component.html',
  styleUrls: ['./referral-popup.component.scss'],
  standalone: true,
  imports: [FaIconComponent],
})
export class ReferralPopupComponent implements OnInit, OnDestroy {
  showPopup = false;
  referralLink = '';
  urlCopied = false;

  private urlCopiedTimer: ReturnType<typeof setTimeout> | null = null;
  private rafClickHandler: ((e: Event) => void) | null = null;

  faCopy = faCopyIcon;
  faX = faX;

  private readonly marketingSyncUserService = inject(MarketingSyncUserService);
  private readonly ambassadorGet = inject(AmbassadorGetService);
  private readonly envService = inject(EnvService);
  private readonly storageService = inject(StorageService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.urlCopiedTimer !== null) {
      clearTimeout(this.urlCopiedTimer);
    }
    this.removeRafTriggerDelegation();
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const brandConfig = this.envService.get.brandConfig;

    if (!brandConfig?.ambassadorIntegrationId) {
      return;
    }

    if (this.storageService.get(DISMISSED_KEY) === 'true') {
      return;
    }

    this.marketingSyncUserService.syncUserReady$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      const win = this.document.defaultView as unknown as SiteWindow | null;

      if (response?.success && response.data?.email) {
        if (win) {
          win.aCustEmail = response.data.email;
        }

        if (response.data.ambassadorUrl) {
          this.referralLink = `${response.data.ambassadorUrl}${UTM_SUFFIX}`;
          this.showPopup = true;
        }

        const envMode = this.envService.get.envMode;
        const integrationId =
          envMode !== 'prod' ? ACE_DEV_AMBASSADOR_INTEGRATION_ID : brandConfig.ambassadorIntegrationId;

        // Identify immediately to beat the embed's anonymous auto-identify on page load.
        this.identifyAndAttach(response.data.email, integrationId);

        // Enrollment runs in parallel; auto_create=1 makes it idempotent for existing ambassadors.
        // EPS-4794: goes through the xcc-api BFF, so no GetAmbassador credential ships in this
        // bundle. The minted url in the response is discarded -- the link shown to the user comes
        // from `response.data.ambassadorUrl` above; this call exists purely for the enrolment.
        this.ambassadorGet
          // `true`: master's `useAmbassador()` hardcoded `email_new_ambassador=1` and this call was
          // subscribed, so the popup has always triggered the vendor's confirmation mail. Safe here
          // -- the address comes from the authenticated sync response, not user input.
          .getReferralLink(response.data.email, true)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => undefined,
            error: () => undefined,
          });
      } else {
        this.showPopup = false;
        this.referralLink = '';
        if (win) {
          win.aCustEmail = undefined;
        }
        this.removeRafTriggerDelegation();
      }
    });
  }

  dismiss(): void {
    this.storageService.set(DISMISSED_KEY, 'true');
    this.showPopup = false;
  }

  copyReferralLink(): void {
    const nav = this.document.defaultView?.navigator;
    if (!nav?.clipboard?.writeText) {
      return;
    }
    void nav.clipboard
      .writeText(this.referralLink)
      .then(() => {
        this.urlCopied = true;
        if (this.urlCopiedTimer !== null) {
          clearTimeout(this.urlCopiedTimer);
        }
        this.urlCopiedTimer = setTimeout(() => {
          this.urlCopied = false;
          this.urlCopiedTimer = null;
        }, 2000);
      })
      .catch(() => {
        this.urlCopied = false;
      });
  }

  private identifyAndAttach(email: string, integrationId: number): void {
    // Identify hangs off the loader stub `window.mbsy` (queues calls until the
    // embed JS hydrates). `window._mbsy` only appears after the embed loads
    // and exposes `integrations` for the trigger — see attachRafTriggerDelegation.
    const win = this.document.defaultView as unknown as
      | (SiteWindow & {
          mbsy?: { identify?: (args: { email: string }) => unknown };
          mbsyIdentify?: string;
        })
      | null;
    const mbsy = win?.mbsy;
    if (!mbsy?.identify) {
      this.attachRafTriggerDelegation(integrationId);
      return;
    }
    Promise.resolve(mbsy.identify({ email }))
      .then(() => {
        setTimeout(() => {
          if (win) {
            win.mbsyIdentify = 'true';
          }
          this.attachRafTriggerDelegation(integrationId);
        }, 500);
      })
      .catch(() => {
        this.attachRafTriggerDelegation(integrationId);
      });
  }

  private attachRafTriggerDelegation(integrationId: number): void {
    if (this.rafClickHandler) {
      return;
    }
    this.rafClickHandler = (e: Event) => {
      const target = e.target as HTMLElement | null;
      const win = this.document.defaultView as unknown as SiteWindow | null;
      if (target?.closest?.('#raf-trigger') && win?._mbsy?.integrations?.[integrationId]?.trigger?.action) {
        win._mbsy.integrations[integrationId].trigger.action();
      }
    };
    this.document.body.addEventListener('click', this.rafClickHandler, false);
  }

  private removeRafTriggerDelegation(): void {
    if (!this.rafClickHandler) {
      return;
    }
    this.document.body.removeEventListener('click', this.rafClickHandler, false);
    this.rafClickHandler = null;
  }
}
